import {Button} from '@wandb/components';
import {LinkNoCrawl} from '@wandb/weave/common/util/links';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
import {useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Card} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import emptyImg from '../assets/il-no-projects.png';
import emptyImg2x from '../assets/il-no-projects@2x.png';
import ImageIconHeader from '../components/ImageIconHeader';
import ProjectCard from '../components/ProjectCard';
import {Project} from '../graphql/projects';
import CreateProjectDrawer from '../pages/HomePage/HomePageSidebar/CreateProjectDrawer';
import {Graph} from '../types/graphql';
import {allProjects, newBenchmarkPage} from '../util/urls';
import {useFeatureFlagCreateBenchmarkEnabled} from '../util/useFeatureFlag';
import EmptyWatermark from './EmptyWatermark';
import {useNavContext, useNavUpdaterContext} from './NavContextProvider';

export type ProjectListViewMode = `tile` | `table`;

export const StyledSeeAllLink = styled(LinkNoCrawl)`
  color: #aaa;
  float: right;
  margin-top: 16px;
`;
export const SeeAllLink = ({to}: {to: string}) => (
  <StyledSeeAllLink to={to}>See all ➞</StyledSeeAllLink>
);

export const ExpandableProjectListHeader = ({
  entityName,
  isReadOnly,
}: {
  entityName: string;
  isReadOnly?: boolean;
}) => {
  const enableCreateBenchmark =
    useFeatureFlagCreateBenchmarkEnabled(entityName);
  const {toggleCreateProjectDrawer} = useNavUpdaterContext();
  const {isCreatingProject} = useNavContext();
  const history = useHistory();

  return (
    <>
      <ImageIconHeader icon="project" text="Projects" />
      {!isReadOnly && (
        <TailwindContents>
          <div className="align-center flex gap-8">
            {enableCreateBenchmark && (
              <Button
                variant="secondary"
                onPress={() => {
                  history.push(newBenchmarkPage(entityName));
                }}>
                New benchmark
              </Button>
            )}
            <Button onPress={toggleCreateProjectDrawer} startIcon="add-new">
              New project
            </Button>
            <CreateProjectDrawer
              open={isCreatingProject}
              entityName={entityName}
              toggleProjectDrawer={toggleCreateProjectDrawer}
            />
          </div>
        </TailwindContents>
      )}
    </>
  );
};

interface ExpandableProjectListProps {
  entityName: string;
  isReadOnly?: boolean;
  projects: Graph<Project> | null;
  suppressHeader?: boolean;
}

const ExpandableProjectList: React.FC<ExpandableProjectListProps> = ({
  entityName,
  isReadOnly,
  projects,
  suppressHeader,
}) => {
  // Sort on the frontend because sorting by lastActive on the backend is too slow.
  const sortedProjects = (projects?.edges ?? [])
    .map(edge => edge.node)
    // TODO: Change to backend reserved list of projects we want hidden
    .filter(n => n.name !== 'model-registry')
    .sort(
      (a, b) =>
        new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
    );

  return (
    <div className="expandable-project-list-wrapper">
      {!suppressHeader && (
        <ExpandableProjectListHeader
          entityName={entityName}
          isReadOnly={isReadOnly}
        />
      )}
      {sortedProjects.length > 0 ? (
        <div className="expandable-project-list">
          <Card.Group>
            {sortedProjects.slice(0, 6).map(p => (
              <ProjectCard key={p.id} project={p} isReadOnly={isReadOnly} />
            ))}
          </Card.Group>
          <SeeAllLink to={allProjects(entityName)} />
        </div>
      ) : (
        <EmptyWatermark
          imageSource={emptyImg}
          imageSource2x={emptyImg2x}
          header="No projects yet."
          details={
            isReadOnly
              ? "This user or team hasn't created any projects yet."
              : 'Create a project to start tracking your experiments.'
          }
          wide
        />
      )}
    </div>
  );
};
export default ExpandableProjectList;
